/**
 * Push-notification registration wrapper.
 *
 * On the public web build this is a no-op: `Capacitor.isNativePlatform()`
 * returns false, so we never hit the native APIs and the bundle keeps
 * working as a normal PWA.
 *
 * Inside the Capacitor Android wrapper this:
 *  1. Requests notification permission (Android 13+ requires runtime).
 *  2. Calls `PushNotifications.register()` to obtain the FCM device token.
 *  3. POSTs the token to `/api/me/devices` so the backend can target
 *     this device.
 *  4. Installs listeners for foreground push events and tap routing.
 *
 * Phase 1 of the Android rollout depends on the user installing the
 * Capacitor plugins locally — see /app/docs/ANDROID_PHASE1_SETUP.md.
 * Until those plugins are present this file falls back to dynamic
 * imports that fail silently, so the web build is unaffected.
 */

import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const APP_VERSION = '1.0.0';

let registered = false;

const isCapacitorAvailable = () => {
  try {
    const { Capacitor } = require('@capacitor/core');
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Subscribe the current device to FCM. Idempotent — calling it twice in
 * one session is a no-op after the first success.
 * Call this from the AuthProvider once the user is logged in.
 */
export async function registerForPushNotifications() {
  if (registered) return;
  if (!isCapacitorAvailable()) return;
  let PushNotifications;
  try {
    PushNotifications = require('@capacitor/push-notifications').PushNotifications;
  } catch {
    return; // plugin not installed yet — web build path
  }
  try {
    const perm = await PushNotifications.requestPermissions();
    if (perm.receive !== 'granted') return;
    await PushNotifications.register();
    PushNotifications.addListener('registration', async ({ value: token }) => {
      try {
        await axios.post(`${API}/me/devices`, {
          token,
          platform: 'android',
          app_version: APP_VERSION,
        });
        registered = true;
      } catch (err) {
        console.warn('FCM token registration failed:', err);
      }
    });
    PushNotifications.addListener('registrationError', (err) => {
      console.warn('FCM registration error:', err);
    });
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action?.notification?.data || {};
      handlePushTap(data);
    });
  } catch (err) {
    console.warn('FCM setup failed:', err);
  }
}

/**
 * Unsubscribe the current device on logout so the backend stops sending
 * pushes to a phone the user no longer owns.
 */
export async function unregisterFromPushNotifications(token) {
  if (!token) return;
  try {
    await axios.delete(`${API}/me/devices/${encodeURIComponent(token)}`);
    registered = false;
  } catch {
    // Not fatal — backend will mark the token dead on first UnregisteredError.
  }
}

function handlePushTap(data) {
  // Route the user to the right place based on the data payload sent
  // from the backend (see fcm.py for the contract).
  const type = data?.type;
  if (type === 'workout_assigned' && data.date) {
    window.location.href = `/calendar?date=${data.date}`;
  } else if (type === 'comment_reply' && data.workout_id) {
    window.location.href = `/workout/${data.workout_id}`;
  } else if (type === 'direct_message') {
    window.location.href = '/messages';
  } else if (type === 'weekly_feedback') {
    window.location.href = '/messages';
  } else if (type === 'morning_reminder') {
    window.location.href = '/';
  }
}
